import {
  IonAlert,
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonIcon,
  IonInput,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  IonText,
  IonTextarea
} from "@ionic/react";
import {
  alertOutline,
  informationCircleOutline,
  musicalNoteOutline
} from "ionicons/icons";
import React, { useEffect, useState } from "react";

import { useAssignments } from "../data/store/useAssignments";
import { useConfig } from "../data/store/useConfig";
import { useProfile } from "../data/store/useProfile";
import { useSongs } from "../data/store/useSongs";
import { useToasts } from "../data/store/useToasts";
import { useTransmission } from "../data/store/useTransmission";
import { ArtistId } from "../models/Artist";

import ArtistSelector from "./ArtistSelector";
import Content from "./Content";
import ConvertingUploader from "./ConvertingUploader";
import HashTagify from "./Hashtagify";
import RequestAccessCard from "./RequestAccessCard";
import SongItem from "./SongItem";

import "react-dropzone-uploader/dist/styles.css";
import { useHelp } from "../data/store/useHelp";
import { usePageTitle } from "../util/sugar";
import Uploader from "./Uploader";
import { ENABLE_GROUPS } from "../config/env";

interface TransmitProps {}

const Transmit: React.FC<TransmitProps> = () => {
  const { authorId, ownerId } = useProfile();
  const {
    todaysTransmission,
    clearTodayTransmission,
    status,
    isValid,
    setAuthors,
    fetchTodaysTransmission,
    setMedia,
    setDescription,
    setTitle,
    transmit,
    setGroups,
    setAssignment,
    assignment,
    setArt,
  } = useTransmission();
  const [selectedGroup] = useTransmission((x) => x.groups || []);
  const { assignmentsInGroup, assignments, fetchAssignmentsInGroup } =
    useAssignments();
  const { post } = useToasts();
  const { delete: deleteSong, fetch, shouldFetch } = useSongs();
  const { config, groups } = useConfig();
  const membership = useConfig((x) => x.activeMemberships());
  const [authors, setSongAuthors] = useState<ArtistId[]>([]);
  const [addSecondSong, setAddSecondSong] = useState(false);
  const transmitNotice = config && config.transmitNotice;
  const openHelp = useHelp((x) => x.open);
  usePageTitle("Transmit a Song");
  const [showArt, setShowArt] = useState(false);
  const [showRevokeAlert, setShowRevokeAlert] = useState(false);
  useEffect(() => {
    if (typeof selectedGroup === "undefined" && membership.length === 1) {
      setGroups([membership[0]]);
    }
  }, [selectedGroup, membership, setGroups]);

  // No per-song visibility picker: SongService.transmit falls back to the
  // artist's own profile privacy (then the instance default) whenever the
  // transmission's `privacy` is left unset, which it always is now that
  // nothing in this form sets it.

  useEffect(() => {
    selectedGroup &&
      assignmentsInGroup(selectedGroup).length === 0 &&
      fetchAssignmentsInGroup(selectedGroup);
  }, [selectedGroup, fetchAssignmentsInGroup,assignmentsInGroup]);

  useEffect(() => {
    const assignmentsList =
      selectedGroup && assignments ? assignmentsInGroup(selectedGroup) : [];
    const activeAssignments = assignmentsList.filter(
      (x) => x.status === "active"
    );
    if (activeAssignments.length === 1) {
      setAssignment(activeAssignments[0].id);
    }
    if (activeAssignments.length === 0) {
      setAssignment("");
    }
  }, [selectedGroup, assignmentsInGroup, setAssignment, assignments]);

  useEffect(() => {
    ownerId && status.includes("idle") && fetchTodaysTransmission(ownerId);

    // eslint-disable-next-line
  }, [fetchTodaysTransmission, ownerId, todaysTransmission]);

  useEffect(() => {
    todaysTransmission &&
      shouldFetch(todaysTransmission) &&
      fetch(todaysTransmission, true);
    // eslint-disable-next-line
  }, [todaysTransmission]);

  useEffect(() => {
    if (authorId && !authors.includes(authorId)) {
      setSongAuthors((oldAuthors) => [...oldAuthors, authorId]);
    } else {
      setAuthors(authors);
    }
  }, [authorId, setSongAuthors, authors, setAuthors]);

  if (ENABLE_GROUPS && membership.length === 0) {
    return (
      <Content>{authorId && <RequestAccessCard authorId={authorId} />}</Content>
    );
  }
  return (
    <Content className="chill">
      {(!todaysTransmission || addSecondSong) && ownerId && (
        <IonRow>
          <IonCol sizeMd="6" pushMd="3">
            {transmitNotice && (
              <IonCard color="light">
                <IonCardContent color="medium">
                  <IonItem color="clear">
                    <HashTagify>{transmitNotice}</HashTagify>
                    <IonButtons slot="end">
                      <IonButton
                        color="tertiary"
                        aria-label="Help information"
                        onClick={() => {
                          openHelp("welcome");
                        }}
                      >
                        <IonIcon icon={informationCircleOutline} aria-hidden="true" />
                      </IonButton>
                    </IonButtons>
                  </IonItem>
                </IonCardContent>
              </IonCard>
            )}

            <IonList className="inner-chill">
              {authorId && authors.includes(authorId) && (
                <ArtistSelector
                  placeholder={"Collaborators"}
                  fieldName={"Authors"}
                  members={authors}
                  setMembers={setSongAuthors}
                />
              )}

              <IonItem>
                <IonLabel position="stacked" color="primary">
                  Title
                </IonLabel>
                <IonInput
                  aria-label="Song title"
                  spellCheck={false}
                  autocapitalize="off"
                  onIonChange={(e) => setTitle(e.detail.value!)}
                  required
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked" color="primary">
                  Description
                </IonLabel>
                <IonTextarea
                  aria-label="Song description"
                  rows={5}
                  maxlength={2000}
                  autoGrow
                  onIonChange={(e) => setDescription(e.detail.value!)}
                />
              </IonItem>
              <IonItem>
                <IonLabel onClick={()=>{
                     setShowArt(!showArt);               
                }} position="stacked" color="primary">
                  Add Artwork
                </IonLabel>
                {showArt&&<Uploader
                  message="select your song artwork"
                  collection="avatar"
                  onComplete={(media) => {
                    setArt(media);
                  }}
                />}
              </IonItem>
              <IonItem>
                <IonLabel position="stacked" color="primary">
                 Song Media
                </IonLabel>
                <ConvertingUploader
                  collection={"songs"}
                  onComplete={(media) => {
                    if (media) setMedia(media);
                  }}
                />
              </IonItem>

              {ENABLE_GROUPS &&
                <IonItem lines="none">
                  <IonLabel
                    position="stacked"
                    color="primary"
                    style={{
                      flex: "1 0 auto",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      overflow: "visible",
                    }}
                  >
                    Group
                    <IonButton
                      fill="clear"
                      color="tertiary"
                      aria-label="Group help information"
                      onClick={() => {
                        openHelp("group");
                      }}
                      style={{
                        ["--padding-start" as any]: "2px",
                        ["--padding-end" as any]: "2px",
                        ["--padding-top" as any]: "0",
                        ["--padding-bottom" as any]: "0",
                        margin: 0,
                        height: "1em",
                        overflow: "visible",
                      }}
                    >
                      {/* Ionic's default icon size inside a button is larger
                          than this button's constrained 1em height, so the
                          button's own overflow clipping cut off the icon's
                          top. Sizing the icon to match the button's own
                          height keeps it fully visible. */}
                      <IonIcon
                        icon={informationCircleOutline}
                        aria-hidden="true"
                        style={{ fontSize: "1em" }}
                      />
                    </IonButton>
                  </IonLabel>
                  <IonRow
                    className="ion-justify-content-end ion-wrap"
                    style={{ margin: "0 -5px" }}
                  >
                    {membership.map((groupId) => {
                      const { title } = groups[groupId] || { title: groupId };
                      return (
                        <IonBadge
                          key={groupId}
                          style={{ cursor: "pointer", margin: 5 }}
                          onClick={() => setGroups([groupId])}
                          color={
                            selectedGroup === groupId ? "favorite" : "medium"
                          }
                        >
                          <IonLabel>
                            <h4>{title}</h4>
                          </IonLabel>
                        </IonBadge>
                      );
                    })}
                  </IonRow>
                </IonItem>
              }

              {ENABLE_GROUPS && selectedGroup &&
                assignmentsInGroup(selectedGroup).length > 0 && (
                  <>
                    <IonItem lines="none">
                      <IonLabel
                        position="stacked"
                        color="primary"
                        style={{ flex: "1 0 auto" }}
                      >
                        Assignment
                      </IonLabel>
                      <IonRow
                        className="ion-justify-content-end ion-wrap"
                        style={{ margin: "0 -5px" }}
                      >
                        {assignmentsInGroup(selectedGroup).map((assign) =>
                          assign.title ? (
                            <IonBadge
                              key={assign.id}
                              style={{ cursor: "pointer", margin: 5 }}
                              onClick={() => setAssignment(assign.id)}
                              color={
                                assign.id === assignment ? "favorite" : "medium"
                              }
                            >
                              <IonLabel>
                                <h4>{assign.title}</h4>
                              </IonLabel>
                            </IonBadge>
                          ) : (
                            <div />
                          )
                        )}
                      </IonRow>
                    </IonItem>

                    {!assignment && (
                      <IonItem lines="none" className="ion-text-center">
                        <IonText
                          color="danger"
                          style={{ margin: "-5px auto 5px" }}
                        >
                          <small>
                            Please select which assignment this is for before
                            submitting
                          </small>
                        </IonText>
                      </IonItem>
                    )}
                  </>
                )}

              {ENABLE_GROUPS && selectedGroup &&
                assignmentsInGroup(selectedGroup).length === 0 && (
                  <IonItem lines="none" className="ion-text-center">
                    <IonText color="danger" style={{ margin: "5px auto" }}>
                      <small>
                        Your instructor hasn't added an assignment for this
                        group yet — you won't be able to submit until they do.
                      </small>
                    </IonText>
                  </IonItem>
                )}
            </IonList>
            <IonRow>
              <IonCol>
                <IonButton
                  disabled={
                    !isValid() ||
                    status === "transmitting" ||
                    (ENABLE_GROUPS && (typeof selectedGroup === "undefined" || !assignment))
                  }
                  color={isValid() ? "favorite" : "primary"}
                  expand="block"
                  onClick={async () => {
                    post({
                      id: "new-song",
                      message: "Song Transmitting",
                      icon: musicalNoteOutline,
                    });
                    try {
                      await transmit(ownerId);
                      setAddSecondSong(false);
                    } catch (e) {
                      post({
                        id: "new-song",
                        message: "Transmission failed — please try again",
                        icon: musicalNoteOutline,
                      });
                    }
                  }}
                >
                  Transmit
                </IonButton>
              </IonCol>
            </IonRow>
          </IonCol>
        </IonRow>
      )}

      {todaysTransmission && !addSecondSong && (
        <>
          <IonRow>
            <IonCol sizeMd="6" pushMd="3">
              <SongItem id={todaysTransmission} />
              <IonButton
                color="danger"
                expand="block"
                onClick={() => setShowRevokeAlert(true)}
              >
                Revoke
              </IonButton>
              <IonAlert
                isOpen={showRevokeAlert}
                onDidDismiss={() => setShowRevokeAlert(false)}
                header="Confirm"
                message="Are you sure you want to revoke this song?"
                buttons={[
                  {
                    text: "Cancel",
                    role: "cancel",
                    cssClass: "secondary"
                  },
                  {
                    text: "Yes, Revoke",
                    handler: () => {
                      deleteSong(todaysTransmission);
                      clearTodayTransmission();
                      post({
                        id: `new-song ${todaysTransmission}`,
                        message: "Song Revoked",
                        icon: alertOutline,
                      });
                    }
                  }
                ]}
              />
            </IonCol>
          </IonRow>
        </>
      )}
    </Content>
  );
};

export default Transmit;
